import { currencyFormatter } from '@/helpers/currencyFormatter';
import { useGetFinancingDataQuery } from '@/services/accounts';
import { Badge, Box, Table } from '@palmetto/palmetto-components';
import type { Column } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { type CSSProperties, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { PAYMENT_TYPE, PaymentMethod, PaymentTypeDescription } from 'types';

import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUserIncludingRoot } from '../auth/RequireIsInternalUser';

function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

function formatDate(dateStr: string): string {
  return DateTime.fromISO(dateStr, { zone: 'utc' }).toFormat('LLL d, yyyy');
}

function getDescription(type: PAYMENT_TYPE): string {
  switch (type) {
    case PAYMENT_TYPE.ACH_PAYMENT_FAILURE:
      return 'Failed ACH Payment';
    case PAYMENT_TYPE.CREDIT_CARD_PAYMENT_FAILURE:
      return 'Failed Credit Card Payment';
    default:
      return 'Payment';
  }
}

function getMethodLabel(method: PaymentMethod): string {
  switch (method) {
    case PaymentMethod.ach:
      return 'ACH';
    case PaymentMethod.creditCard:
      return 'Credit Card';
    case PaymentMethod.check:
      return 'Check';
    case PaymentMethod.cash:
      return 'Cash';
    case PaymentMethod.other:
      return 'Other';
    default:
      return '—';
  }
}

function getPendingMethodLabel(description: PaymentTypeDescription): string {
  switch (description) {
    case PaymentTypeDescription.ONE_TIME_ACH:
      return 'ACH';
    case PaymentTypeDescription.ONE_TIME_CC:
      return 'Credit Card';
    default:
      return '—';
  }
}

type HistoryRow = {
  _key: string;
  date: string;
  description: string;
  method: string;
  status: 'pending' | 'paid' | 'failed';
  amount: number;
};

const TABLE_WRAPPER_STYLE: CSSProperties = { maxHeight: '490px', overflowY: 'auto', width: '100%' };
const EMPTY_PAYMENTS: never[] = [];

const columns: Column[] = [
  {
    heading: 'DATE',
    dataKey: 'date',
    render: (cell: unknown) => formatDate(cell as string),
  },
  {
    heading: 'DESCRIPTION',
    dataKey: 'description',
    render: (cell: unknown) => cell as string,
  },
  {
    heading: 'METHOD',
    dataKey: 'method',
    render: (cell: unknown) => cell as string,
  },
  {
    heading: 'STATUS',
    dataKey: 'status',
    render: (cell: unknown) => {
      const status = cell as HistoryRow['status'];
      if (status === 'pending') return <Badge variant="warning" message="Pending" />;
      if (status === 'paid') return <Badge variant="success" message="Paid" />;
      return <Badge variant="danger" message="Failed" />;
    },
  },
  {
    heading: 'AMOUNT',
    dataKey: 'amount',
    render: (cell: unknown) => formatCurrency(cell as number),
  },
];

export function CustomerPaymentHistory() {
  const { id = '' } = useParams<{ id: string }>();
  const { claims } = useAuth();
  const { data: financingData, isLoading } = useGetFinancingDataQuery(id, {
    skip: !id || !isPalmettoFinanceUserIncludingRoot(claims),
  });

  const rows = useMemo<HistoryRow[]>(() => {
    const pendingRows: HistoryRow[] = (financingData?.pendingPayments ?? EMPTY_PAYMENTS).map((p) => ({
      _key: `pending-${p.paymentId}`,
      date: p.paymentDate,
      description: 'One-Time Payment',
      method: getPendingMethodLabel(p.paymentTypeDescription),
      status: 'pending',
      amount: p.amount,
    }));

    const historyRows: HistoryRow[] = (financingData?.payments ?? EMPTY_PAYMENTS).map((p, i) => ({
      _key: `${p.date}-${p.type}-${i}`,
      date: p.date,
      description: getDescription(p.type),
      method: getMethodLabel(p.paymentMethod),
      status: p.type === PAYMENT_TYPE.PAYMENT ? 'paid' : 'failed',
      // Ledger/legacy amounts arrive as strings; coerce for the numeric row model.
      amount: Number(p.amount),
    }));

    return [...pendingRows, ...historyRows];
  }, [financingData?.pendingPayments, financingData?.payments]);

  return (
    <Box childGap="md" margin="0 0 2xl 0">
      <Box childGap="2xs">
        <Box as="h3" fontSize="md" fontWeight="bold">
          PAYMENT HISTORY
        </Box>
        <Box fontSize="sm" color="body-secondary">
          Payments will not display immediately. Allow 1-4 days to be processed.
        </Box>
      </Box>
      <Box style={TABLE_WRAPPER_STYLE}>
        <Table rowKey="_key" columns={columns} rows={rows} isLoading={isLoading} hasStickyHeader />
      </Box>
    </Box>
  );
}
